'use client';

import { useMounted } from './useMounted';
import { useDisplayTimeZone } from '@/contexts/DisplayTimeZoneContext';

/**
 * Timezone to use when formatting datetimes for display in a client component.
 *
 * Inside a `DisplayTimeZoneProvider` with a zone (an event with
 * `event.timezone` set) it returns that zone on every render — server and
 * client agree, so no hydration gate is needed and times never flip to the
 * viewer's clock.
 *
 * Otherwise it returns `'UTC'` during SSR and the first client render —
 * deterministic across the server and every viewer, so server HTML === first
 * client render and there is no React #418 hydration mismatch. After mount it
 * returns `undefined`, i.e. the viewer's local timezone (our display rule).
 *
 * Usage: `formatTime(iso, locale, useHydratedTimeZone())` or
 * `date.toLocaleTimeString(locale, { ..., timeZone: useHydratedTimeZone() })`.
 */
export function useHydratedTimeZone(): string | undefined {
  const pinned = useDisplayTimeZone();
  const mounted = useMounted();
  if (pinned) return pinned;
  return mounted ? undefined : 'UTC';
}
